import { useCallback, useEffect, useState } from 'react'

interface UseVideoPreviewOptions {
  fetchId: string | null | undefined
  fetchVideoUrl: (id: string) => Promise<string>
}

interface UseVideoPreviewReturn {
  videoUrl: string | null
  videoOpen: boolean
  videoStartTime: number
  videoResumeTime: number | undefined
  handlePlayRecording: (currentTime?: number) => void
  setVideoOpen: (open: boolean) => void
  setVideoResumeTime: (time: number) => void
}

export function useVideoPreview({
  fetchId,
  fetchVideoUrl,
}: UseVideoPreviewOptions): UseVideoPreviewReturn {
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [videoOpen, setVideoOpen] = useState(false)
  const [videoStartTime, setVideoStartTime] = useState(0)
  const [videoResumeTime, setVideoResumeTime] = useState<number | undefined>(undefined)

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchVideoUrl identity is unstable (mutation fn)
  useEffect(() => {
    if (!fetchId || videoUrl) return
    async function loadVideoUrl() {
      try {
        const url = await fetchVideoUrl(fetchId as string)
        setVideoUrl(url)
      } catch {
        // Video URL fetch is best-effort — profile works without it
      }
    }
    loadVideoUrl()
  }, [fetchId])

  const handlePlayRecording = useCallback((currentTime?: number) => {
    setVideoStartTime(currentTime ?? 0)
    setVideoOpen(true)
  }, [])

  return {
    videoUrl,
    videoOpen,
    videoStartTime,
    videoResumeTime,
    handlePlayRecording,
    setVideoOpen,
    setVideoResumeTime,
  }
}
